import { useEffect, useState } from "react";
import supabase from "../config/supabaseConfig";
import { City, Location } from "../types/locationType";

interface LocationsData {
  states: Location[];
  cities: City[];
}

// Shared by every component that shows locations (one download per page, not one
// per component), kept for a few minutes so the ad counts don't go stale for long.
const CACHE_MS = 5 * 60 * 1000;
let cache: { at: number; promise: Promise<LocationsData> } | null = null;

const fetchLocations = async (): Promise<LocationsData> => {
  const [
    { data: statesData, error: statesError },
    { data: citiesData, error: citiesError },
    { data: stateAdCounts, error: stateCountError },
    { data: cityAdCounts, error: cityCountError },
  ] = await Promise.all([
    supabase.from("states").select("*"),
    supabase.from("cities").select("*, states(name)"),
    supabase.rpc('count_ads_by_state'),
    supabase.rpc('count_ads_by_city'),
  ]);

  if (statesError || citiesError) throw new Error("Échec de la récupération des données");
  if (stateCountError || cityCountError) {
    console.error("Error fetching ad counts:", stateCountError || cityCountError);
  }

  // Create map of counts for easier lookup
  const stateCountMap = new Map<string, number>();
  const cityCountMap = new Map<string, number>();

  stateAdCounts?.forEach((count: { state_id: string, count: number }) =>
    stateCountMap.set(count.state_id, count.count)
  );
  cityAdCounts?.forEach((count: { city_id: string, count: number }) =>
    cityCountMap.set(count.city_id, count.count)
  );

  // Add counts to states
  const statesWithCounts = (statesData || []).map(state => ({
    ...state,
    adCount: stateCountMap.get(state.id) || 0
  }));

  // Add counts to cities
  const citiesWithCounts = (citiesData || []).map(city => ({
    ...city,
    parentLocationName: city.states.name,
    adCount: cityCountMap.get(city.id) || 0
  }));

  return { states: statesWithCounts, cities: citiesWithCounts };
};

const loadLocations = (): Promise<LocationsData> => {
  if (cache && Date.now() - cache.at < CACHE_MS) return cache.promise;
  const promise = fetchLocations().catch((err) => {
    cache = null; // don't keep a failure: the next component tries again
    throw err;
  });
  cache = { at: Date.now(), promise };
  return promise;
};

const useFetchAllLocations = () => {
  const [states, setStates] = useState<Location[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    loadLocations()
      .then((data) => {
        if (cancelled) return;
        setStates(data.states);
        setCities(data.cities);
      })
      .catch(() => {
        // same as before: a failure leaves the lists empty
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return {
    states,
    cities,
    loading,
  };
};

export default useFetchAllLocations;
